import BellIcon from '../../assets/logo/BellIcon';
import IllustrationExplainer from '../IllustrationExplainer';
import { useTranslation } from 'react-i18next';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import useRouter from '../../hooks/navigation/useRouter';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { SCREENS } from '../../services/exports/Constants';
import { Routes } from '../../modules/mobile/exports/Enums';

interface Props {
  /**
   * Where the secondary action leads. `checkout` is wrong when the error IS the checkout
   * screen — SCREENS.CHECKOUT renders OrderConfirmationScreen, so going there would
   * re-render this same page and strand the customer.
   */
  backTo?: 'checkout' | 'basket';
  /**
   * The draft failed because the delivery address is missing or was rejected. Retrying
   * cannot help — the address gate short-circuits before any request — so the primary
   * action opens the order-method modal instead.
   */
  isDeliveryDetails?: boolean;
}

/**
 * Shown when the order draft could not be created.
 *
 * The payment and confirmation screens cannot render without a draft, so before this
 * existed a failed draft left a full-screen loader up with no way out — the customer
 * could neither pay nor leave. Failing visibly, with a way forward and a way back, is what
 * keeps any future draft failure (network, closed restaurant, stale basket) recoverable.
 */
export default function OrderDraftErrorPage(props: Props) {
  const { backTo = 'checkout', isDeliveryDetails = false } = props;

  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderDraftErrorPage' });

  const { retryDraft } = useOrderFlow();
  const { toggleOrderMethodModal } = useAppMethods();
  const { navigate } = useRouter();

  const goBack = (): void =>
    backTo === 'basket'
      ? navigate({ web: { to: SCREENS.BASKET }, mobile: { path: Routes.Cart } })
      : navigate({ web: { to: SCREENS.CHECKOUT }, mobile: { path: Routes.Checkout } });

  const addAddress = (): void => toggleOrderMethodModal({ openModal: true, showReturn: true });

  return (
    <main className={container} data-test="order-draft-error">
      <IllustrationExplainer
        illustration={<BellIcon className="h-20 w-20" />}
        title={isDeliveryDetails ? t('address_header') : t('header')}
        description={isDeliveryDetails ? t('address_text') : t('text')}
      />
      <div className={buttonContainer}>
        {isDeliveryDetails ? (
          <button type="button" onClick={addAddress} className={button} data-test="order-draft-error-address">
            {t('address_action')}
          </button>
        ) : (
          <button type="button" onClick={retryDraft} className={button} data-test="order-draft-error-retry">
            {t('retry')}
          </button>
        )}
        <button type="button" onClick={goBack} className={secondaryButton} data-test="order-draft-error-back">
          {backTo === 'basket' ? t('back_basket') : t('back_checkout')}
        </button>
      </div>
    </main>
  );
}

const container =
  'bg-brand-background-primary fixed inset-0 z-50 flex flex-col h-full w-full justify-center items-center lg:w-1/2 mx-auto px-md';

const buttonContainer = 'lg:mt-xl mt-lg flex flex-col items-center gap-sm';

const button =
  'inline-flex items-center justify-center rounded-[9999px] bg-black text-white text-sm font-semibold px-xl py-sm hover:opacity-90 transition-opacity';

const secondaryButton =
  'inline-flex items-center justify-center rounded-[9999px] text-sm font-semibold px-xl py-sm underline hover:opacity-90 transition-opacity';
